import siteMapService from '../../../services/site-map-service/site-map-service';
import { ASTUTE, SUPPORT_HUB_REGEX } from '../../../support-constants';
import localStorageWrapper from '../../../../components/utils/local-storage-wrapper/localStorageWrapper';
import { SlackMessages } from '../../../../services/slack-message-service/SlackMessageService';
import { onAnchorClick } from '../astute-topic';
import { CategoriesProps } from '../../browse-by-topic/hook/use-bbt-content';

interface TopicResponseData {
    topicResponse: any;
    category: string;
    subcategory: string;
    currentRoot: string;
    subCategoryPath: string;
    bbtContent: CategoriesProps | null;
    serverContext: any;
}

interface TopicResponseDataWithScripts extends TopicResponseData {
    scriptsToLoad: () => void;
    fireAdobeAnalytics: () => void;
}

export default function getRedirectedWithBBT(
    bbtContent: any,
    category: string,
    subcategory: string,
    currentRoot: string,
    subCategoryPath: string
) {
    const currentRootPath =
        category && subcategory
            ? `${currentRoot}${subCategoryPath}/`
            : `${currentRoot}#bbt`;

    window.location.href = bbtContent ? currentRootPath : `${currentRoot}/`;
}

export const getTopicRedirectionPath = (
    topicResponse: any,
    category: string,
    subcategory: string,
    currentRoot: string,
    subCategoryPath: string,
    bbtContent: any,
    serverContext: any
) => {
    let errorMessage = '';
    if (topicResponse.data.error) {
        errorMessage = topicResponse.data.error;
    } else if (topicResponse.data.topicAbsoluteSeo) {
        const topicSplit = topicResponse?.data?.topicAbsoluteSeo.split('/');
        const canonicalCategory = topicSplit[1];
        const canonicalSubCategory = topicSplit[2];
        if (canonicalCategory !== category) {
            errorMessage = SlackMessages.CATEGORY_PAGE_INVALID_URL;
        } else if (canonicalSubCategory !== subcategory) {
            errorMessage = SlackMessages.SUBCATEGORY_PAGE_INVALID_URL;
        } else {
            errorMessage = SlackMessages.OLD_TOPIC_PAGE_URL;
        }
    } else {
        errorMessage = SlackMessages.TOPIC_PAGE_INVALID_URL;
    }
    const topicResponseData = {
        bbtContent,
        category,
        subcategory,
        currentRoot,
        subCategoryPath,
        serverContext,
        topicResponse,
    };

    errorMessage &&
        sendErrorMessageAndRedirect(errorMessage, topicResponseData);
};

export const sendErrorMessageAndRedirect = async (
    errorMessage: string,
    topicResponseData: TopicResponseData
) => {
    const topicPath = siteMapService
        .getPathFromRouterConfigs(
            topicResponseData.serverContext.currentLanguageRegionCode,
            topicResponseData.serverContext.brand,
            'AstuteTopic',
            topicResponseData.topicResponse?.data?.topicAbsoluteSeo
                ?.substring(1)
                .split('/') || []
        )
        ?.concat(
            topicResponseData.topicResponse?.data?.topicAbsoluteSeo?.endsWith(
                '/'
            )
                ? '/'
                : ''
        );
    if (
        errorMessage === topicResponseData.topicResponse?.data?.error ||
        errorMessage === SlackMessages.TOPIC_PAGE_INVALID_URL
    ) {
        getRedirectedWithBBT(
            topicResponseData.bbtContent,
            topicResponseData.category,
            topicResponseData.subcategory,
            topicResponseData.currentRoot,
            topicResponseData.subCategoryPath
        );
    } else {
        window.location.href = `${topicResponseData.currentRoot}${topicPath}`;
    }
};

export const processSearchParam = () => {
    const searchParams = new URLSearchParams(window?.location?.search);
    const sspCampaignIdStorageNm = 'SSP_CAMPAIGN_ID';

    if (
        searchParams.has(ASTUTE.CAMPAIGN_ID_SEARCH_PARAM) &&
        localStorageWrapper.getItem(sspCampaignIdStorageNm) !==
            searchParams.get(ASTUTE.CAMPAIGN_ID_SEARCH_PARAM)
    ) {
        localStorageWrapper.setItem(
            sspCampaignIdStorageNm,
            searchParams.get(ASTUTE.CAMPAIGN_ID_SEARCH_PARAM) || ''
        );
    } else if (
        !searchParams.has(ASTUTE.CAMPAIGN_ID_SEARCH_PARAM) &&
        localStorageWrapper.getItem(sspCampaignIdStorageNm)
    ) {
        localStorageWrapper.removeItem(sspCampaignIdStorageNm);
    }
};

export const getCategoryNameFromTopicResponse = (topicResponse: any) => {
    return topicResponse?.data?.categoryName || '';
};

export const getSubCategoryNameFromTopicResponse = (topicResponse: any) => {
    return topicResponse?.data?.subCategoryName || '';
};

export const getTopicLabelFromTopicResponse = (topicResponse: any) => {
    return topicResponse?.data?.topicLabel || '';
};

export const getCurrentLanguageRegionCodeFromServerContext = (
    serverContext: any
) => {
    return serverContext.currentLanguageRegionCode
        ? serverContext.currentLanguageRegionCode.split('-')[0]
        : '';
};

export const getCurrentRootFromServerContext = (serverContext: any) => {
    return serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';
};

export const getDocumentTitleFromTopicPage = (topicPage: any) => {
    return (
        topicPage.topicLabel &&
        topicPage.topicLabel
            .toString()
            .replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
            .replace(SUPPORT_HUB_REGEX.FIND_ALL_TABS_NEW_LINE, '')
            .replace(SUPPORT_HUB_REGEX.NON_BREAKING_SPACE, ' ')
            .replace(SUPPORT_HUB_REGEX.FIND_FIRST_110_CHARS, '$1')
    );
};

export const getBbtErrorMessageForCategoryPage = (
    bbtContent: any,
    categoriesResponse: any
) => {
    let errorMessage = '';
    if (bbtContent && bbtContent.hideBrowseByTopic) {
        errorMessage = SlackMessages.BBT_CONTENT_DISABLED;
    } else if (categoriesResponse?.data?.error) {
        errorMessage = categoriesResponse.data.error;
    } else if (!categoriesResponse?.data?.filteredCategories) {
        errorMessage = SlackMessages.CATEGORY_PAGE_INVALID_URL;
    }
    return errorMessage;
};

export const getBbtErrorMessageForSubCategoryPage = (
    bbtContent: any,
    categoriesResponse: any
) => {
    let errorMessage = '';
    if (bbtContent && bbtContent.hideBrowseByTopic) {
        errorMessage = SlackMessages.BBT_CONTENT_DISABLED;
    } else if (
        categoriesResponse?.data?.filteredCategories &&
        !categoriesResponse?.data?.filteredSubCategories
    ) {
        errorMessage = categoriesResponse?.data?.error
            ? categoriesResponse?.data?.error
            : SlackMessages.SUBCATEGORY_PAGE_INVALID_URL;
    } else if (!categoriesResponse?.data?.filteredCategories) {
        errorMessage = SlackMessages.CATEGORY_PAGE_INVALID_URL;
    }
    return errorMessage;
};

export const getCategoryNameFromCategoriesResponse = (
    categoriesResponse: any
) => {
    return categoriesResponse?.data?.filteredCategories?.categoryName || '';
};

export const getSubCategoryNameFromCategoriesResponse = (
    categoriesResponse: any
) => {
    return (
        categoriesResponse?.data?.filteredSubCategories?.subCategoryName || ''
    );
};

export const loadScriptsForAstuteTopicData = (
    props: TopicResponseDataWithScripts
) => {
    if (props.topicResponse?.data) {
        if (!props.topicResponse.data.bodyWoHeading) {
            getTopicRedirectionPath(
                props.topicResponse,
                props.category,
                props.subcategory,
                props.currentRoot,
                props.subCategoryPath,
                props.bbtContent,
                props.serverContext
            );
        } else {
            props.scriptsToLoad();
            props.fireAdobeAnalytics();
            document.addEventListener('click', onAnchorClick, false);
        }
    }
};
